import { HttpClient } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import { DataStateChangeEventArgs } from '@syncfusion/ej2-angular-grids';
import { Observable, throwError, Subject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpHelper } from '../helpers/http-helper';
import { IServerSettings, IDashboardRequest, IPaginatedResult } from '../models/index';
import { AuthenticationService } from './authentication.service';
import { SettingsService } from './settings.service';
import { StorageManagerService } from './storage-manager.service';

@Injectable()
export class LLDashboardService extends Subject<DataStateChangeEventArgs> {

  private action: string;
  private settings: IServerSettings;
  private apiUrl: string;
  httpHelper: HttpHelper;

   constructor (private http: HttpClient, private settingsService: SettingsService, private authService: AuthenticationService, private storageManagerService: StorageManagerService) {
    super();

    // Define an instance of the http helper class that will be used to create httpHeader for api calls
    this.httpHelper = new HttpHelper(http, authService, storageManagerService);
  }

  // Get a list of LL forms for the dashboard
  getLLForms(): Observable<any[]> {
    const headers = this.httpHelper.createHttpHeaders();
    this.settings = this.settingsService.getSettings();
    this.apiUrl = this.settings.ApiGatewayUrl;
    this.action = `ssdpds/v1/LLForms/LLDashboard`;

    return this.http.get(`${ this.apiUrl }/${ this.action }`, { headers: headers })
      .pipe(map(this.extractData),
        catchError(this.handleErrors));
  }

  // Get all non-deleted LL revisions for the SPL Upload existing-product dropdown
  getLLUploadRevisionOptions(): Observable<any[]> {
    const headers = this.httpHelper.createHttpHeaders();
    this.settings = this.settingsService.getSettings();
    this.apiUrl = this.settings.ApiGatewayUrl;
    this.action = `ssdpds/v1/LLForms/LLDashboard?includeUploadRevisionOptions=true`;

    return this.http.get(`${ this.apiUrl }/${ this.action }`, { headers: headers })
      .pipe(map(this.extractData),
        catchError(this.handleErrors));
  }

  // Get paginated orders for a specific product (LL Revision) with server-side filtering and sorting
  getProductDashboardPaginated(llRevisionId: number, request: IDashboardRequest): Observable<IPaginatedResult<any>> {
    const headers = this.httpHelper.createHttpHeaders();
    this.settings = this.settingsService.getSettings();
    this.apiUrl = this.settings.ApiGatewayUrl;
    this.action = `ssdpds/v1/LLForms/ProductDashboardPaginated/${llRevisionId}`;

    return this.http.post(`${ this.apiUrl }/${ this.action }`, request, { headers: headers })
      .pipe(map(this.extractData),
        catchError(this.handleErrors));
  }

   // Get a paginated list of LL forms with server-side filtering and sorting
  getLLFormsPaginated(request: IDashboardRequest): Observable<IPaginatedResult<any>> {
    const headers = this.httpHelper.createHttpHeaders();
    this.settings = this.settingsService.getSettings();
    this.apiUrl = this.settings.ApiGatewayUrl;
    this.action = 'ssdpds/v1/LLForms/LLDashboardPaginated';

    return this.http.post(`${this.apiUrl}/${this.action}`, request, { headers: headers })
      .pipe(map(this.extractData),
        catchError(this.handleErrors));
  }

  // Get a paginated list of LL Merge deliveries with server-side filtering and sorting
  getLLMergeDeliveryDashboardPaginated(request: IDashboardRequest): Observable<IPaginatedResult<any>> {
    const headers = this.httpHelper.createHttpHeaders();
    this.settings = this.settingsService.getSettings();
    this.apiUrl = this.settings.ApiGatewayUrl;
    this.action = 'ssdpds/v1/LLForms/LLMergeDeliveryDashboardPaginated';

    return this.http.post(`${this.apiUrl}/${this.action}`, request, { headers: headers })
      .pipe(map(this.extractData),
        catchError(this.handleErrors));
  }

  // Get a list of LL deliveries for the dashboard
   getLLDelilveries(): Observable<any[]> {
    const headers = this.httpHelper.createHttpHeaders();
    this.settings = this.settingsService.getSettings();
    this.apiUrl = this.settings.ApiGatewayUrl;
    this.action = 'ssdpds/v1/LLForms/LLMergeDeliveryDashboard';

    return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
        .pipe(map(this.extractData),
          catchError(this.handleErrors));
  }

  downloadFile(filePath: string, fileName: string) {
    const headers = this.httpHelper.createHttpHeaders();
    const settings = this.settingsService.getSettings();
    const apiUrl = settings.ApiGatewayUrl;
    const action = `ssdpds/v1/FileStorage/DownloadFile?filePath=${filePath}&fileName=${fileName}`;
    return this.http.get(`${apiUrl}/${action}`, { headers: headers, responseType: 'blob' })
        .pipe(map(response => response),
          catchError(this.handleErrors));
}


  private extractData(response: Response) {
    if (response.status < 200 || response.status >= 300) {
      throw new Error('Bad response status: ' + response.status);
    }

    const body = response;
    return body || {};
  }

  // Error handling
  private handleErrors(error: any): Observable<any> {
    const GENERIC_ERROR_MSG = 'It looks like something went wrong. Try reloading the page or contacting support if this issue continues.';
    const bDisplayDetailMsg = error && error.error && error.error.isBusinessErrorMessage ? true : isDevMode() ? true : false;
    error.error = error && error.error && error.error.message && bDisplayDetailMsg ? error.error.message : GENERIC_ERROR_MSG;

    return throwError(error.error);
  }
}
